import type { QuickLoginRow } from "@observe-tech/utils";
import { useReducer, useRef } from "react";
import { useTranslation } from "react-i18next";
import type {
  PinLoginOutcome,
  QuickLoginSuccess,
} from "../../../../shell-handle/quickLogin";
import { PIN_LENGTH } from "../components/PinInput";
import { PIN_LOGIN_FAILURES } from "../pinLoginFailures";
import type { QuickLoginController } from "./useQuickLogin";

interface PinState {
  account: QuickLoginRow | null;
  value: string;
  error?: string;
  locked: boolean;
  pending: boolean;
}

type PinAction =
  | { type: "select"; account: QuickLoginRow }
  | { type: "typed"; value: string }
  | { type: "submitted" }
  | { type: "failed"; error: string; lock: boolean }
  | { type: "reset" };

const CLEARED = { value: "", error: undefined, locked: false, pending: false };

function reducer(state: PinState, action: PinAction): PinState {
  switch (action.type) {
    case "select":
      return { ...CLEARED, account: action.account };
    case "typed":
      return { ...state, value: action.value, error: undefined };
    case "submitted":
      return { ...state, pending: true };
    case "failed":
      return {
        ...state,
        value: "",
        error: action.error,
        locked: action.lock,
        pending: false,
      };
    case "reset":
      return { ...state, ...CLEARED };
  }
}

export interface PinLoginOptions {
  quickLogin: QuickLoginController | null;
  onSuccess(outcome: QuickLoginSuccess): void | Promise<void>;
  onAccountGone(left: QuickLoginRow[]): void;
  onError(error: unknown): void;
}

export function usePinLogin({
  quickLogin,
  onSuccess,
  onAccountGone,
  onError,
}: PinLoginOptions) {
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(reducer, {
    account: null,
    ...CLEARED,
  });
  const inFlight = useRef(false);

  const handleOutcome = async (outcome: PinLoginOutcome) => {
    if (outcome.status === "online" || outcome.status === "offline") {
      return onSuccess(outcome);
    }
    if (outcome.status === "notFound") {
      const left = (await quickLogin?.refresh()) ?? [];
      dispatch({ type: "reset" });
      return onAccountGone(left);
    }
    const {
      message,
      offlineMessage = message,
      lock,
    } = PIN_LOGIN_FAILURES[outcome.status];
    const online =
      offlineMessage === message || ((await quickLogin?.probeOnline()) ?? true);
    const error = t(online ? message : offlineMessage);
    dispatch({
      type: "failed",
      error:
        outcome.status === "wrongPin"
          ? `${error} ${t("Attempts left:")} ${outcome.attemptsLeft}`
          : error,
      lock,
    });
  };

  const change = (value: string) => {
    dispatch({ type: "typed", value });
    const { account } = state;
    if (value.length < PIN_LENGTH || !quickLogin || !account) return;
    if (inFlight.current) return;
    inFlight.current = true;
    dispatch({ type: "submitted" });
    quickLogin
      .pinLogin(account.cognitoSub, value)
      .then(handleOutcome)
      .catch((error) => {
        dispatch({ type: "reset" });
        onError(error);
      })
      .finally(() => {
        inFlight.current = false;
      });
  };

  const select = (account: QuickLoginRow) => {
    inFlight.current = false;
    dispatch({ type: "select", account });
  };

  return {
    account: state.account,
    value: state.value,
    error: state.error,
    pending: state.pending,
    disabled: state.pending || state.locked,
    select,
    change,
  };
}
